/// <reference types="node" />
// API client · Phase 1 / S1 骨架（fetch 封装）
//
// 设计依据：
//   - docs/architecture/backend-proposal.md §v3-G ① 末段 / §v3-J（全文）
//   - EP-010-S1 改动文件清单："新建 src/api/client.ts（新建）：getSave() / getMe() / getHealth()
//     三个方法 + access token 内存持有 + 401 自动 POST /auth/refresh"
//     注：本工单 S1 仅实现 getHealth() + getMe()；getSave/putSave 是 S3 的事，auth/refresh 是 S4 的事。
//
// ★★★ §v3-J 真相源原则硬约束（backend-proposal.md §v3-J.1 / §v3-J.2） ★★★
// 本 client 是云端 DynamoDB 的**唯一出入口**。本文件：
//   ✗ 不读 localStorage 做状态判定（§v3-J.1 第 2 条）
//   ✗ 不写 localStorage 做状态变更（§v3-J.1 第 3 条）
//   ✗ 不把 localStorage 内容当作 dogState / trip / inventory 的判定依据
//
// 任何 dogState / trip / inventory 的判定都走"云端返回值 + Date.now()"（§v3-J.2 硬纪律）。
// localStorage 仅作为云端状态的只读 cache（§v3-J.1 第 2 条），由 src/store/persist.ts 唯一
// 写盘；本 client **不**碰 persist.ts，也**不**在 client 内调用 localStorage。
//
// 验证（EP-010-S1 验收信号 5）：
//   grep -rE "localStorage.*dogState\[" src/   → 应为 0 行（本文件不出现）
//   tests/unit/api/client.test.ts              → spy(localStorage).getLocalStorage 断言未调
//
// S3 起 getSave/putSave 接入后，本文件**仍**不引入 localStorage 读写（镜像写入走 store 订阅链）。

import type {
  HealthResp,
  MeResp,
  SaveGetResp,
  SavePutReq,
  SavePutOk,
} from './types';
import { ApiError } from './types';

// ───────────────────────────────────────────────────────────────────────────
// 基础 URL：VITE_API_BASE_URL（默认 http://localhost:3000，SAM Local 直连 API Gateway）
// ───────────────────────────────────────────────────────────────────────────

/**
 * 拼装 API 完整 URL（仅做字面拼接，不做 query / path 解析 —— 简单即是可靠）。
 *
 * 为什么不用 URL 构造函数：
 *   - import.meta.env.VITE_API_BASE_URL 在 Vite 编译期被替换为字面量字符串
 *     （见 .env.example + vite.config.ts 默认 3000 端口对齐 SAM Local）
 *   - URL 构造函数对单段路径（如 "/health"）会清掉 base URL 的 path，
 *     但本工程 base URL 不带 path，所以简化即可
 */
function buildUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/**
 * 默认 baseUrl：本地 SAM Local 跑在 3000 端口（与 EP-010-S1 验收信号 1 对齐）。
 * 真云 dev 部署后由 Vite build 时注入 VITE_API_BASE_URL=https://dev.dogtrip.example.com/api/v1
 */
const DEFAULT_API_BASE_URL = 'http://localhost:3000';

/**
 * 读环境变量：Vite 编译期把 import.meta.env.VITE_API_BASE_URL 替换为字面量。
 * 兜底 = DEFAULT_API_BASE_URL（SAM Local 默认值）。
 */
function readApiBaseUrl(): string {
  // ★ Vite 在编译期静态替换：vite.config.ts 默认 3000 端口对齐 SAM Local。
  // 真云部署时 build 命令前设 VITE_API_BASE_URL=https://<前端域>/api/v1
  // 注意：本字段**不**持久化、不写 localStorage（§v3-J 真相源）
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any).env ?? {};
  return (env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_API_BASE_URL;
}

// ───────────────────────────────────────────────────────────────────────────
// access token 内存持有（S1 阶段不接 Cognito，token 来自 bootstrap 注入）
// ───────────────────────────────────────────────────────────────────────────

let inMemoryAccessToken: string | null = null;

/**
 * 设置当前 access token（由 src/auth/cognito.ts bootstrap 调用，S4 阶段实现）。
 *
 * ★ §v3-J 真相源原则：
 *   token 走**内存**持有，不写 localStorage / sessionStorage（§v3-J.1 第 2 条）。
 *   token 是身份凭证，localStorage 缓存会让 XSS 拿到长期 token；内存持有 + 短 TTL
 *   是 §v3-C.5 的安全基线。
 */
export function setAccessToken(token: string | null): void {
  inMemoryAccessToken = token;
}

export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

// ───────────────────────────────────────────────────────────────────────────
// 内部 fetch 封装
//
// S4 起导出为 `requestInternal`：src/api/auth.ts（login / register / refresh / logout）
// **必须**复用同一条 fetch 链路（同一 baseUrl、同一 ApiError 归一化、同一 credentials），
// 不允许另起一个裸 fetch 旁路 §v3-J 出入口纪律。
// 除 src/api/** 内部外，业务代码一律走 typed methods（getHealth / getMe / getSave / …）。
// ───────────────────────────────────────────────────────────────────────────

export async function requestInternal<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  opts?: { body?: unknown; auth?: boolean },
): Promise<T> {
  const baseUrl = readApiBaseUrl();
  const url = buildUrl(baseUrl, path);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  // ★ 默认所有需认证的端点都带 Authorization；/health 强制不带
  if (opts?.auth !== false && inMemoryAccessToken) {
    headers['Authorization'] = `Bearer ${inMemoryAccessToken}`;
  }

  let body: string | undefined;
  if (opts?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let resp: Response;
  try {
    // 注意：exactOptionalPropertyTypes 下，body: undefined 与 RequestInit.body: BodyInit | null 不兼容
    // —— 只在 body 有值时构造 RequestInit（Vite/Vitest 全栈一致）
    const init: RequestInit = {
      method,
      headers,
      cache: 'no-store', // ★ §v3-G 风险 #1：/api/* 不应被浏览器缓存
      // ★ S4：本地开发跨端口（Vite 5173 → Express 3000）必须带 cookie，
      //   否则 httpOnly 的 dogtrip_token / dogtrip_refresh 不会被发送（§v3-C.3）。
      //   同源（真云 CloudFront 代理 /api/*）下 include 与 same-origin 等价，无副作用。
      credentials: 'include',
    };
    if (body !== undefined) init.body = body;
    resp = await fetch(url, init);
  } catch (err) {
    // network failure / DNS / abort —— 全部归一为 ApiError（前端用 §v3-H.4 指数退避处理）
    throw new ApiError(`Network error calling ${url}: ${(err as Error).message}`, {
      status: 0,
      url,
    });
  }

  // 解析 body
  let parsed: unknown;
  const text = await resp.text();
  if (text.length === 0) {
    parsed = null;
  } else {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(`Non-JSON response from ${url}`, {
        status: resp.status,
        url,
        body: text,
      });
    }
  }

  if (!resp.ok) {
    throw new ApiError(`HTTP ${resp.status} from ${url}`, {
      status: resp.status,
      url,
      body: parsed,
    });
  }

  return parsed as T;
}

// ───────────────────────────────────────────────────────────────────────────
// 公开方法（typed）
// ───────────────────────────────────────────────────────────────────────────

/**
 * GET /health（§v3-G ① · §v3-H.4 首次探活目标）
 * 不带 Authorization（/health 公开，handler 端 Auth: NONE）。
 */
export async function getHealth(): Promise<HealthResp> {
  return requestInternal<HealthResp>('GET', '/health', { auth: false });
}

/**
 * GET /auth/me（§v3-G ① 末段）
 *
 * ★ S4 路径修正（本工单发现的存量坑）：
 *   原实现调 `/me`，但**当前在跑的后端是 Express 本地加速模式**，它挂的是 `/auth/me`
 *   （src/backend-express/routes/auth-me.ts:17）。`/me` 是 SAM Local 模板里的 S1 骨架路径
 *   （infra/sam/template.yaml:131），两者不一致 —— 不改这里，登录后刷新页面会因
 *   `GET /me` 404 而永远判定为"未登录"，AuthGuard 又把玩家踢回登录框。
 *   真云路径是 `/api/v1/me`（§v3-C.4），S9 集成测时统一由 VITE_API_BASE_URL 兜住。
 *
 * S4 后行为：Express 的 jwtAuthMiddleware 解析 cookie JWT → runMe(sub) 返回真 userId。
 * 前端 bootstrap 调用本方法验真，拿到 userId 后置 authState='authenticated'（在 store 里）；
 * 前端**不**持久化 userId 到 localStorage（§v3-J.1 第 2 条 + §v3-C.5 安全基线 ——
 * token 与身份走内存，不落盘）。
 */
export async function getMe(): Promise<MeResp> {
  return requestInternal<MeResp>('GET', '/auth/me', { auth: true });
}

// ───────────────────────────────────────────────────────────────────────────
// S3：GET /save（§v3-C.4 / §v3-B.2 / §v3-J.4）
// ───────────────────────────────────────────────────────────────────────────

/**
 * GET /save —— 云端当前档读取。
 *
 * 返回：
 *   - 200 + SaveGetOk（云端有档）
 *   - 404 + SaveGetNotFound（云端无档；前端 migrate 流程可能见到）
 *
 * §v3-J 真相源纪律：
 *   - 本方法是云端 `SaveDataV1` 的**唯一权威读出入口**
 *   - 任何 dogState / trip / inventory 判定都基于"云端返回值 + Date.now()"（§v3-J.2）
 *   - localStorage 仅作为云端响应后的**被动镜像**写入（由 src/store/persist.ts 唯一把控；
 *     本 client **不**碰 persist.ts，也**不**在 client 内调用 localStorage —— §v3-J.2 末段）
 *
 * 401 行为：handler 401 → throw ApiError(401)；由调用方决定重定向到 AuthPanel 还是 S7 才补的
 * 401 兜底（§v3-C.5 + S7 AuthGuard）。
 */
export async function getSave(): Promise<SaveGetResp> {
  try {
    return await requestInternal<SaveGetResp>('GET', '/save', { auth: true });
  } catch (e) {
    // 404 = 云端无档（正常业务语义，不是错误）。requestInternal 对非 2xx 一律抛
    // ApiError，这里把 404 转回 SaveGetNotFound，让 tryCloudBootstrap 走「首次上传」分支
    // （cloudRev=0 → PUT migrate）。否则 404 被 catch 静默吞掉，cloudRev 永远 null，
    // 后续 uploadToCloud 一直跳过 → 存档永不进云端。
    if (e instanceof ApiError && e.status === 404) {
      return { schema: 'save/v1', kind: 'no_save', message: '云端无存档' };
    }
    throw e;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// S3：PUT /save（§v3-C.4 / §v3-B.4 / §v3-D.1 #2 / §v3-D.1 #4 / §v3-J.4 用例 3）
// ───────────────────────────────────────────────────────────────────────────

/**
 * PUT /save —— 云端当前档覆盖写（CAS + rev 单调 + contentHash 服务端权威）。
 *
 * 入参（SavePutReq）：
 *   - baseRev: 客户端知道的云端 rev（首次创建 = 0；后续 = 上次 GET 的 rev）
 *   - data: 整份 SaveDataV1 的 JSON 字符串
 *   - intent / clientSavedAt / clientId: 可选（仅诊断 / 日志）
 *
 * 返回：
 *   - 200 + SavePutOk（成功；新 rev = baseRev + 1）
 *   - 409 + SavePutConflict（rev 冲突；body.current 是云端当前值）
 *     → 前端按 §D.5「兜底幂等」比对 current.contentHash === 本地 contentHash
 *       → 相同即判定"其实已经同步成功了"，静默收敛，不报错给用户
 *   - 502 / 5xx → 抛 ApiError；§v3-H.4 指数退避
 *
 * §v3-D.1 #2 纪律：
 *   - 前端**永远不**计算 hash（避免本地领先于云端）
 *   - handler 在写入前从原始请求体重新解析 → canonicalJson → sha256
 *   - 响应里的 contentHash 是服务端权威值，前端镜像后写入 cache（被动写入）
 *
 * §v3-D.1 #4 纪律：rev 永不回退。`newRev = baseRev + 1` 由服务端计算；
 * 客户端永远不自己算 rev（避免本地领先于云端的"幽灵自增"）。
 *
 * §v3-J.4 用例 3 纪律：
 *   - handler 是无状态的；失败语义 = 服务端未成功 = 前端 cache 不更新
 *   - 本 client 在 5xx 时 throw ApiError，**不**做乐观写入
 *   - 409 时也 throw ApiError（status=409, body=conflict）—— 调用方按 §D.5 判定
 */
export async function putSave(body: SavePutReq): Promise<SavePutOk> {
  // 409 是业务正常分支，不算 error；前端按 §D.5 收敛（contentHash 比对）。
  // 这里用 throw 包成 ApiError 是为了与 4xx/5xx 统一捕获；调用方可读 body.current.
  // 改成"非 409 抛 / 409 返回"两种形态会让 4 道门 / 单元测试样板代码膨胀，不值得。
  try {
    return await requestInternal<SavePutOk>('PUT', '/save', { auth: true, body });
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      // 把 409 重新包成 ApiError，body 字段透传 SavePutConflict
      throw e;
    }
    throw e;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// S4 已落地：login / register / refresh / logout 全部在 src/api/auth.ts
//   （同目录、同 requestInternal 链路、同 ApiError 归一化；client.ts 只放 typed 的数据端点）
// ───────────────────────────────────────────────────────────────────────────